import pipelineWebhooksAPI from 'dashboard/api/pipeline/webhooks';
import { createAccountRequestGuard } from './utils/accountRequestGuard';
import { createOperationRequestTracker } from './utils/operationRequestTracker';

const fetchRequestGuard = createAccountRequestGuard();
const mutationRequestTracker = createOperationRequestTracker();
const normalizeAccountId = accountId => {
  if (accountId === undefined || accountId === null) return null;
  const normalized = String(accountId).trim();
  return normalized || null;
};
const resolveAccountId = (rootGetters, accountId) =>
  normalizeAccountId(
    accountId === undefined ? rootGetters.getCurrentAccountId : accountId
  );
const requireAccountId = (rootGetters, accountId) => {
  const resolvedAccountId = resolveAccountId(rootGetters, accountId);
  if (resolvedAccountId === null) {
    throw new TypeError('accountId is required');
  }
  return resolvedAccountId;
};
const accountIsCurrent = (rootGetters, accountId) => {
  const activeAccountId = normalizeAccountId(rootGetters.getCurrentAccountId);
  return (
    accountId !== null &&
    activeAccountId !== null &&
    activeAccountId === accountId
  );
};
const normalizeIdPayload = payload =>
  payload && typeof payload === 'object'
    ? payload
    : { id: payload, accountId: undefined };

const state = {
  requestEpoch: 0,
  webhooks: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isTesting: false,
  },
};

const getters = {
  allWebhooks: $state => $state.webhooks,
  uiFlags: $state => $state.uiFlags,
};

const actions = {
  get: async ({ commit, rootGetters, state: $state }, payload = {}) => {
    const accountId = requireAccountId(rootGetters, payload.accountId);
    const request = fetchRequestGuard.begin($state, {
      accountId,
      generation: payload.generation,
    });
    const requestEpoch = $state.requestEpoch;
    const isCurrentRequest = () =>
      requestEpoch === $state.requestEpoch &&
      fetchRequestGuard.isCurrent($state, request, rootGetters);
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await pipelineWebhooksAPI.get(accountId);
      if (!isCurrentRequest()) return [];
      commit('SET_WEBHOOKS', data.payload);
      return data.payload;
    } catch (error) {
      if (isCurrentRequest()) throw error;
      return [];
    } finally {
      if (isCurrentRequest()) {
        commit('SET_UI_FLAG', { isFetching: false });
      }
    }
  },

  create: async ({ commit, rootGetters, state: $state }, webhookObj = {}) => {
    const accountId = requireAccountId(rootGetters, webhookObj.accountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'isCreating',
    });
    const requestIsCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId) &&
      mutationRequestTracker.isLatest($state, operation);
    const payload = { ...webhookObj };
    delete payload.accountId;
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await pipelineWebhooksAPI.create(payload, accountId);
      if (!requestIsCurrent()) return undefined;
      commit('ADD_WEBHOOK', data);
      return data;
    } finally {
      const completion = mutationRequestTracker.finish($state, operation);
      if (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId)
      ) {
        commit('SET_UI_FLAG', { isCreating: false });
      }
    }
  },

  update: async (
    { commit, rootGetters, state: $state },
    { id, accountId: rawAccountId, ...updateObj }
  ) => {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'isUpdating',
      identity: id,
    });
    const requestIsCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId) &&
      mutationRequestTracker.isLatest($state, operation);
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await pipelineWebhooksAPI.update(
        id,
        updateObj,
        accountId
      );
      if (!requestIsCurrent()) return undefined;
      commit('UPDATE_WEBHOOK', data);
      return data;
    } finally {
      const completion = mutationRequestTracker.finish($state, operation);
      if (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId)
      ) {
        commit('SET_UI_FLAG', { isUpdating: false });
      }
    }
  },

  delete: async ({ commit, rootGetters, state: $state }, rawPayload) => {
    const { id, accountId: rawAccountId } = normalizeIdPayload(rawPayload);
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'isDeleting',
      identity: id,
    });
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await pipelineWebhooksAPI.delete(id, accountId);
      if (
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId) &&
        mutationRequestTracker.isLatest($state, operation)
      ) {
        commit('DELETE_WEBHOOK', id);
      }
    } finally {
      const completion = mutationRequestTracker.finish($state, operation);
      if (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId)
      ) {
        commit('SET_UI_FLAG', { isDeleting: false });
      }
    }
  },

  test: async ({ commit, rootGetters, state: $state }, rawPayload) => {
    const { id, accountId: rawAccountId } = normalizeIdPayload(rawPayload);
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'isTesting',
      identity: id,
    });
    const requestIsCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId) &&
      mutationRequestTracker.isLatest($state, operation);
    commit('SET_UI_FLAG', { isTesting: true });
    try {
      const { data } = await pipelineWebhooksAPI.test(id, accountId);
      return requestIsCurrent() ? data : undefined;
    } finally {
      const completion = mutationRequestTracker.finish($state, operation);
      if (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId)
      ) {
        commit('SET_UI_FLAG', { isTesting: false });
      }
    }
  },

  regenerateSecret: async (
    { commit, rootGetters, state: $state },
    rawPayload
  ) => {
    const { id, accountId: rawAccountId } = normalizeIdPayload(rawPayload);
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'regenerateSecret',
      identity: id,
    });
    try {
      const { data } = await pipelineWebhooksAPI.regenerateSecret(
        id,
        accountId
      );
      if (
        requestEpoch !== $state.requestEpoch ||
        !accountIsCurrent(rootGetters, accountId) ||
        !mutationRequestTracker.isLatest($state, operation)
      ) {
        return undefined;
      }
      commit('UPDATE_WEBHOOK', data);
      return data;
    } finally {
      mutationRequestTracker.finish($state, operation);
    }
  },

  reset: ({ commit }) => commit('RESET_ACCOUNT_STATE'),
};

const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  SET_WEBHOOKS($state, webhooks) {
    $state.webhooks = webhooks;
  },
  RESET_ACCOUNT_STATE($state) {
    mutationRequestTracker.reset($state);
    $state.requestEpoch = ($state.requestEpoch || 0) + 1;
    $state.webhooks = [];
    $state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isTesting: false,
    };
  },
  ADD_WEBHOOK($state, webhook) {
    $state.webhooks.push(webhook);
  },
  UPDATE_WEBHOOK($state, webhook) {
    const index = $state.webhooks.findIndex(w => w.id === webhook.id);
    if (index !== -1) {
      $state.webhooks.splice(index, 1, webhook);
    }
  },
  DELETE_WEBHOOK($state, id) {
    $state.webhooks = $state.webhooks.filter(w => w.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
